import { useState, useEffect, useCallback } from 'react';

// Date fields that need to be revived from JSON strings
const DATE_FIELDS = ['scheduledDate', 'approvedAt', 'createdAt'];

/**
 * Revives Date objects from JSON strings
 */
function reviveDates<T>(data: T): T {
  if (data === null || data === undefined) return data;

  if (Array.isArray(data)) {
    return data.map(item => reviveDates(item)) as T;
  }

  if (typeof data === 'object') {
    const revived: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      if (DATE_FIELDS.includes(key) && typeof value === 'string') {
        const date = new Date(value);
        revived[key] = isNaN(date.getTime()) ? value : date;
      } else if (typeof value === 'object') {
        revived[key] = reviveDates(value);
      } else {
        revived[key] = value;
      }
    }
    return revived as T;
  }

  return data;
}

/**
 * Custom hook for localStorage with type safety and JSON serialization
 * Includes date revival for Post dates
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  // Get initial value from localStorage or use provided initial value
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (item) {
        const parsed = JSON.parse(item);
        return reviveDates<T>(parsed);
      }
      return initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Update localStorage when value changes
  const setValue = useCallback((value: T | ((prev: T) => T)) => {
    try {
      setStoredValue(prev => {
        const valueToStore = value instanceof Function ? value(prev) : value;
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
        return valueToStore;
      });
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  }, [key]);

  // Listen for changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          setStoredValue(reviveDates<T>(parsed));
        } catch (error) {
          console.warn(`Error parsing storage event for key "${key}":`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  return [storedValue, setValue];
}
